import { Link, useRouterState } from "@tanstack/react-router";
import { Heart, Menu, Search, ShoppingBag, User, X } from "lucide-react";
import { useEffect, useState } from "react";
import { AnnouncementBar } from "./AnnouncementBar";
import logo from "@/assets/sunset-logo.webp";
import logoAvif from "@/assets/sunset-logo.avif";
import { navLinks } from "@/lib/catalog";
import { useShop } from "@/lib/shop-store";
import { cn } from "@/lib/utils";

function NavItem({ link }: { link: (typeof navLinks)[number] }) {
  return (
    <div className="group/nav relative">
      <Link
        to={link.to}
        className="label-xs link-underline whitespace-nowrap text-[0.55rem] tracking-[0.18em] text-foreground/90 transition-colors hover:text-foreground xl:text-[0.625rem] xl:tracking-[0.22em]"
      >
        {link.label}
      </Link>
      {"children" in link && link.children ? (
        <div className="invisible absolute right-0 top-full z-50 min-w-[10rem] translate-y-1 border border-border bg-background pb-3 pt-3 opacity-0 shadow-[var(--shadow-soft)] transition-all duration-300 group-hover/nav:visible group-hover/nav:translate-y-0 group-hover/nav:opacity-100">
          {link.children.map((c) => (
            <Link
              key={c.label}
              to={c.to}
              className="label-xs block px-5 py-2 text-[0.6rem] text-foreground transition-colors hover:text-accent"
            >
              {c.label}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Logo({ className, scrolled }: { className?: string; scrolled: boolean }) {
  return (
    <Link
      to="/"
      aria-label="Sunset Concept Store — accueil"
      className={cn("flex shrink-0 items-center justify-center leading-none", className)}
    >
      <picture>
        <source type="image/avif" srcSet={logoAvif} />
        <img
          src={logo}
          alt="Sunset Concept Store"
          className={cn(
            "h-auto w-auto transition-all duration-500 ease-[var(--ease-lux)]",
            scrolled ? "h-8 sm:h-10 lg:h-11 xl:h-12" : "h-9 sm:h-11 lg:h-12 xl:h-[3.25rem]",
          )}
        />
      </picture>
    </Link>
  );
}

function Icons({ className }: { className?: string }) {
  const { cartCount, setCartOpen, wishlist } = useShop();
  return (
    <div className={cn("flex items-center justify-end gap-4 sm:gap-5", className)}>
      <button type="button" aria-label="Rechercher" className="p-0.5">
        <Search size={18} strokeWidth={1.3} />
      </button>
      <button type="button" aria-label="Mon compte" className="hidden p-0.5 sm:block">
        <User size={18} strokeWidth={1.3} />
      </button>
      <button type="button" aria-label="Mes favoris" className="relative hidden p-0.5 sm:block">
        <Heart size={18} strokeWidth={1.3} />
        {wishlist.length > 0 ? (
          <span className="absolute -right-2 -top-1.5 text-[0.6rem] text-accent">
            {wishlist.length}
          </span>
        ) : null}
      </button>
      <button
        type="button"
        aria-label="Panier"
        onClick={() => setCartOpen(true)}
        className="relative p-0.5"
      >
        <ShoppingBag size={18} strokeWidth={1.3} />
        <span className="absolute -right-2 -top-1.5 text-[0.6rem] tabular-nums">
          {cartCount}
        </span>
      </button>
    </div>
  );
}

const headerNavLabels = [
  "Nouveautés",
  "Beachwear",
  "Maillots de bain",
  "Kimonos",
  "Sacs",
  "Accessoires",
];
const headerNavLinks = headerNavLabels.map(
  (label) => navLinks.find((l) => l.label === label)!,
);

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const overHero = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [pathname]);

  const solid = scrolled || !overHero || menuOpen;

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <AnnouncementBar />

      <div
        className={cn(
          "transition-all duration-500 ease-[var(--ease-lux)]",
          solid
            ? "border-b border-border/70 bg-background/95 text-foreground backdrop-blur-md"
            : "bg-transparent text-onmedia",
          "lg:border-b lg:border-border/70 lg:bg-background/95 lg:text-foreground lg:backdrop-blur-md",
          scrolled && "shadow-[var(--shadow-soft)]",
        )}
      >
        <div
          className={cn(
            "mx-auto flex max-w-7xl flex-col px-4 transition-all duration-500 ease-[var(--ease-lux)] sm:px-6 lg:px-12 xl:px-16",
            scrolled ? "py-2.5 sm:py-3 lg:py-9" : "py-3.5 sm:py-4 lg:py-10",
          )}
        >
          {/* 3-zone header: icons (left) | logo (center) | categories (right) */}
          <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center lg:gap-x-12 xl:gap-x-20">
            {/* Left zone: icons (desktop) / hamburger (mobile) */}
            <div className="col-start-1 justify-self-start">
              <button
                type="button"
                aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
                onClick={() => setMenuOpen((v) => !v)}
                className="-ml-1 shrink-0 p-1 lg:hidden"
              >
                {menuOpen ? <X size={20} strokeWidth={1.3} /> : <Menu size={20} strokeWidth={1.3} />}
              </button>

              <Icons className="hidden justify-start lg:flex" />
            </div>

            {/* Center: logo */}
            <Logo className="col-start-2 justify-self-center" scrolled={scrolled} />

            {/* Right zone: categories (desktop) */}
            <nav className="col-start-3 hidden flex-col items-end gap-y-1.5 justify-self-end lg:flex">
              <div className="flex items-center justify-end gap-6 xl:gap-8">
                {headerNavLinks.slice(0, 3).map((l) => (
                  <NavItem key={l.to + l.label} link={l} />
                ))}
              </div>
              <div className="flex items-center justify-end gap-6 xl:gap-8">
                {headerNavLinks.slice(3).map((l) => (
                  <NavItem key={l.to + l.label} link={l} />
                ))}
              </div>
            </nav>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={cn(
          "overflow-hidden border-b border-border bg-background transition-[max-height,opacity] duration-500 lg:hidden",
          menuOpen ? "max-h-[32rem] opacity-100" : "max-h-0 opacity-0",
        )}
      >
        <nav className="mx-auto flex max-w-7xl flex-col px-6 py-4">
          {navLinks.map((l) => (
            <div key={l.to + l.label} className="border-b border-border/60 last:border-0">
              <Link to={l.to} className="label-sm block py-4">
                {l.label}
              </Link>
              {"children" in l && l.children ? (
                <div className="-mt-1 flex gap-6 pb-4 pl-4">
                  {l.children.map((c) => (
                    <Link
                      key={c.label}
                      to={c.to}
                      className="label-xs text-[0.6rem] text-muted-foreground"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </nav>
      </div>
    </header>
  );
}
